import React, { useCallback, useEffect, useState } from 'react';
import { CloudArrowUp, Info, WarningCircle } from '@phosphor-icons/react';
import { IosSwitch } from '../../../../components/immobilien/ui';
import {
  ALTARCHIV_ERKLAERUNG, berechneFortschritt, darfAltarchivSteuern, ladeLaufSchalter, ladeMassenSchalter, ladeStatistik, MASSEN_ERKLAERUNG,
  setzeLauf, setzeMassenablage, type AltarchivStatistik, type LaufSchalter, type MassenSchalter,
} from '../../../../lib/dokumente/altarchivUebernahme';
import { deDatumZeit } from '../aktenschrank/dateiDarstellung';

const zahl = (n: number | undefined) => (n ?? 0).toLocaleString('de-DE');

function Kennzahl({ wert, label, farbe }: { wert: number | undefined; label: string; farbe?: string }) {
  return (
    <span className="flex flex-col" style={{ minWidth: 92, padding: '8px 12px', borderRadius: 14, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <span className="immo-zahl" style={{ fontSize: 17, fontWeight: 800, color: farbe ?? 'var(--ink)' }}>{zahl(wert)}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink3)' }}>{label}</span>
    </span>
  );
}

/**
 * Altarchiv-Übernahme (#517, Etappe B): Erklärung, Ein/Aus-Schalter (nur Admins) und Fortschritt des
 * Hintergrundlaufs, der Vorschläge für Name, Ordner und Bezüge erzeugt. Abgelegt wird hier nichts.
 */
export function UebernahmeKarte() {
  const [schalter, setSchalter] = useState<LaufSchalter | null>(null);
  const [massen, setMassen] = useState<MassenSchalter | null>(null);
  const [stat, setStat] = useState<AltarchivStatistik | null>(null);
  const [darf, setDarf] = useState(false);
  const [schaltet, setSchaltet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const lade = useCallback(async () => {
    try {
      const [s, st, m] = await Promise.all([ladeLaufSchalter(), ladeStatistik(), ladeMassenSchalter()]);
      setSchalter(s); setStat(st); setMassen(m);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    }
  }, []);

  useEffect(() => { void lade(); darfAltarchivSteuern().then(setDarf).catch(() => setDarf(false)); }, [lade]);
  // Während der Lauf an ist, Fortschritt alle 15 s auffrischen.
  useEffect(() => {
    if (!schalter?.lauf && !massen?.lauf) return;
    const t = setInterval(() => { void lade(); }, 15_000);
    return () => clearInterval(t);
  }, [schalter?.lauf, massen?.lauf, lade]);

  const umschaltenMassen = async (an: boolean) => {
    if (!massen) return;
    setSchaltet(true); setFehler(null);
    try { await setzeMassenablage(an, massen); setMassen({ ...massen, lauf: an }); await lade(); }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen'); }
    finally { setSchaltet(false); }
  };

  const umschalten = async (an: boolean) => {
    if (!schalter) return;
    setSchaltet(true); setFehler(null);
    try { await setzeLauf(an, schalter); setSchalter({ ...schalter, lauf: an }); await lade(); }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen'); }
    finally { setSchaltet(false); }
  };

  const f = stat ? berechneFortschritt(stat) : null;
  const fertig = !!f && f.gesamt > 0 && f.offen === 0;
  const zustand = !schalter ? '' : fertig ? 'Alle PDFs geprüft' : schalter.lauf ? 'Läuft im Hintergrund' : 'Ausgeschaltet';

  return (
    <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 16, gap: 12 }} aria-label={ALTARCHIV_ERKLAERUNG.titel}>
      <div className="flex items-center" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}>
          <CloudArrowUp weight="fill" size={21} color="var(--acc)" />
        </span>
        <span className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{ALTARCHIV_ERKLAERUNG.titel}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: schalter?.lauf && !fertig ? 'var(--ok)' : 'var(--ink3)' }}>
            {zustand}{stat?.zuletzt ? ` · zuletzt ${deDatumZeit(stat.zuletzt)}` : ''}
          </span>
        </span>
        <IosSwitch an={!!schalter?.lauf} onChange={an => { void umschalten(an); }} label={ALTARCHIV_ERKLAERUNG.titel}
          disabled={!darf || !schalter || schaltet} />
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', margin: 0 }}>{ALTARCHIV_ERKLAERUNG.was}</p>
      <p className="flex items-start" style={{ gap: 6, fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)', margin: 0 }}>
        <Info size={14} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>{ALTARCHIV_ERKLAERUNG.nicht}{!darf && ' Ein- und ausschalten können nur Admins.'}</span>
      </p>

      {f && f.gesamt > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div className="flex items-center justify-between" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
            <span className="immo-zahl">{zahl(f.geprueft)} von {zahl(f.gesamt)} PDFs geprüft</span>
            <span className="immo-zahl">{f.prozent} %</span>
          </div>
          <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={f.prozent} aria-label="Fortschritt"
            style={{ height: 8, borderRadius: 4, background: 'var(--fill2)', overflow: 'hidden' }}>
            <div style={{ width: `${f.prozent}%`, height: '100%', borderRadius: 4, background: 'var(--acc)', transition: 'width .4s' }} />
          </div>
        </div>
      )}

      {stat && (
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <Kennzahl wert={stat.sicherheit.hoch} label="sehr sicher" farbe="var(--ok)" />
          <Kennzahl wert={stat.sicherheit.mittel} label="eher sicher" farbe="var(--warn)" />
          <Kennzahl wert={stat.sicherheit.niedrig} label="unsicher" farbe="var(--bad)" />
          <Kennzahl wert={stat.status.ohne_objekt} label="ohne Objekt" />
          <Kennzahl wert={stat.status.fehler} label="Fehler" />
          <Kennzahl wert={stat.status.duplikat} label="Duplikate" />
        </div>
      )}

      {massen && stat && (
        <div className="immo-innen flex flex-col" style={{ borderRadius: 18, padding: '12px 14px', gap: 6 }}>
          <div className="flex items-center" style={{ gap: 10 }}>
            <span className="flex flex-col flex-1 min-w-0">
              <span style={{ fontSize: 14, fontWeight: 800 }}>{MASSEN_ERKLAERUNG.titel}</span>
              <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 600, color: massen.lauf ? 'var(--ok)' : 'var(--ink3)' }}>
                {massen.lauf ? (stat.ablage_bereit ? 'Läuft im Hintergrund · ' : 'Fertig · ') : 'Ausgeschaltet · '}
                {zahl(stat.status.abgelegt)} übernommen · {zahl(stat.ablage_bereit)} bereit{stat.ablage_fehler ? ` · ${zahl(stat.ablage_fehler)} mit Fehler` : ''}
              </span>
            </span>
            <IosSwitch an={massen.lauf} onChange={an => { void umschaltenMassen(an); }} label={MASSEN_ERKLAERUNG.titel} disabled={!darf || schaltet} />
          </div>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--ink2)' }}>{MASSEN_ERKLAERUNG.was}</p>
          <p className="flex items-start" style={{ margin: 0, gap: 6, fontSize: 11, lineHeight: 1.5, color: 'var(--ink3)' }}>
            <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{MASSEN_ERKLAERUNG.nicht}{!darf && ' Ein- und ausschalten können nur Admins.'}</span>
          </p>
        </div>
      )}

      {fehler && (
        <div role="alert" className="flex items-center" style={{ gap: 8, fontSize: 13, color: 'var(--bad)' }}>
          <WarningCircle weight="fill" size={18} />{fehler}
        </div>
      )}
    </section>
  );
}
